import { useQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { GET_SCHOOL_ASSESSMENT_TYPES } from '@/graphql/Queries/assessmentType';
import {
  CREATE_ASSESSMENT_TYPE,
  UPDATE_ASSESSMENT_TYPE,
  DELETE_ASSESSMENT_TYPE,
  REORDER_ASSESSMENT_TYPES,
} from '@/graphql/mutations/assessment';

import type { Ref } from 'vue';

export interface ManagedAssessmentType {
  id: string;
  name: string;
  description: string | null;
  max_score: number;
  is_ca: boolean;
  is_exam: boolean;
  sequence: number | null;
  subject_id: string | null;
  academic_calendar_id: string | null;
  subject: { id: string; name: string } | null;
  academicCalendar: {
    id: string;
    session: { name: string } | null;
    termStructure: { name: string } | null;
  } | null;
}

export interface AssessmentTypeFilters {
  academic_calendar_id?: string | null;
  subject_id?: string | null;
  is_ca?: boolean;
  is_exam?: boolean;
  include_reusable?: boolean;
}

export interface AssessmentTypeFormValues {
  name: string;
  max_score: number;
  description: string | null;
  is_ca: boolean;
  is_exam: boolean;
  subject_id: string | null;
  academic_calendar_id: string | null;
}

/** GraphQL Int args: coerce a string/empty id to a number or null. */
function toIntOrNull(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function useSchoolAssessmentTypes(filters: Ref<AssessmentTypeFilters>, enabled: Ref<boolean>) {
  const toast = useToast();
  const confirm = useConfirm();

  const { result, loading, refetch } = useQuery(
    GET_SCHOOL_ASSESSMENT_TYPES,
    () => ({ ...filters.value }),
    () => ({ enabled: enabled.value, fetchPolicy: 'network-only' }),
  );

  const assessments = computed<ManagedAssessmentType[]>(
    () => result.value?.schoolAssessmentTypes ?? [],
  );

  const { mutate: createMutate, loading: creating } = useMutation(CREATE_ASSESSMENT_TYPE);
  const { mutate: updateMutate, loading: updating } = useMutation(UPDATE_ASSESSMENT_TYPE);
  const { mutate: deleteMutate } = useMutation(DELETE_ASSESSMENT_TYPE);
  const { mutate: reorderMutate, loading: reordering } = useMutation(REORDER_ASSESSMENT_TYPES);
  const deletingId = ref<string | null>(null);

  function validate(form: AssessmentTypeFormValues): boolean {
    if (!form.name.trim()) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Please enter a name.', life: 3000 });
      return false;
    }
    if (!form.max_score || form.max_score <= 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Please enter a valid max score.', life: 3000 });
      return false;
    }
    return true;
  }

  async function createAssessment(form: AssessmentTypeFormValues): Promise<boolean> {
    if (!validate(form)) return false;
    try {
      await createMutate({
        input: {
          name: form.name.trim(),
          max_score: form.max_score,
          description: form.description?.trim() || null,
          is_ca: form.is_ca,
          is_exam: form.is_exam,
          subject_id: toIntOrNull(form.subject_id),
          academic_calendar_id: toIntOrNull(form.academic_calendar_id),
        },
      });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Assessment type created.', life: 3000 });
      await refetch();
      return true;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Something went wrong.';
      toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
      return false;
    }
  }

  async function updateAssessment(id: string, form: AssessmentTypeFormValues): Promise<boolean> {
    if (!validate(form)) return false;
    try {
      await updateMutate({
        input: {
          id: Number(id),
          name: form.name.trim(),
          max_score: form.max_score,
          description: form.description?.trim() || null,
          is_ca: form.is_ca,
          is_exam: form.is_exam,
          subject_id: toIntOrNull(form.subject_id),
          academic_calendar_id: toIntOrNull(form.academic_calendar_id),
        },
      });
      toast.add({ severity: 'success', summary: 'Success', detail: 'Assessment type updated.', life: 3000 });
      await refetch();
      return true;
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Something went wrong.';
      toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
      return false;
    }
  }

  function confirmDeleteAssessment(item: ManagedAssessmentType) {
    confirm.require({
      message: `Delete "${item.name}"? This cannot be undone.`,
      header: 'Delete Assessment Type',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Cancel',
      acceptLabel: 'Delete',
      acceptClass: 'p-button-danger',
      accept: async() => {
        deletingId.value = item.id;
        try {
          const res = await deleteMutate({ id: item.id });
          if (res?.data?.deleteAssessmentType?.success === false) {
            toast.add({ severity: 'error', summary: 'Error', detail: res.data.deleteAssessmentType.message || 'Failed to delete.', life: 4000 });
            return;
          }
          toast.add({ severity: 'success', summary: 'Deleted', detail: `"${item.name}" deleted.`, life: 3000 });
          await refetch();
        } catch (error: unknown) {
          const msg = error instanceof Error ? error.message : 'Failed to delete.';
          toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
          deletingId.value = null;
        }
      },
    });
  }

  async function reorder(ordered: ManagedAssessmentType[]): Promise<void> {
    const order_data = ordered.map((item, index) => ({ id: item.id, sequence: index + 1 }));
    try {
      await reorderMutate({ order_data });
      toast.add({ severity: 'success', summary: 'Reordered', detail: 'Order saved.', life: 2000 });
      await refetch();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to save order.';
      toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
      await refetch();
    }
  }

  return {
    assessments,
    loading,
    creating,
    updating,
    reordering,
    deletingId,
    refetch,
    createAssessment,
    updateAssessment,
    confirmDeleteAssessment,
    reorder,
  };
}
